import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { icon, notifyIcon } from "../../../../config";
import { getNotification } from "../../../../utils/common";
import { useEmailRules } from "./EmailRulesContext";
import RuleCard from "./RuleCard";
import RuleForm from "./RuleForm";
import "./RuleForm.scss";
import { ActionsSection } from "./formSections";
import { DELETE_ACTION_ID, labels } from "./ruleEngine.constants";
import "./rules.scss";
import { getEmailRules, updateEmailRule } from "./rules.service";

/**
 * Component that displays the list of email rules
 * PC : EmailIntegration
 * @author Sarthak Arora
 */
const RulesList = () => {
  const { rules, loading, error, isFormOpen, setIsFormOpen, setEditingRule, editingRule, openForm, refreshRules, setRules, actions } =
    useEmailRules();
  const [draggedRule, setDraggedRule] = useState(null);
  const [defaultRuleActions, setDefaultRuleActions] = useState([]);
  const isDefaultActionSelected = (actionId) => defaultRuleActions.includes(actionId);

  const deleteRules = useMemo(() => {
    return rules.filter((rule) => {
      if (!rule?.actions) return false;
      const actionIds = rule.actions.split(",").map((id) => parseInt(id.trim()));
      return actionIds.includes(DELETE_ACTION_ID);
    });
  }, [rules]);

  const nonDeleteRules = useMemo(() => {
    return rules.filter((rule) => {
      if (!rule?.actions) return false;
      const actionIds = rule.actions.split(",").map((id) => parseInt(id.trim()));
      return !actionIds.includes(DELETE_ACTION_ID) && !rule.IsDefault;
    });
  }, [rules]);

  const defaultRule = useMemo(() => {
    return rules.find((rule) => rule.IsDefault);
  }, [rules]);

  // Sync defaultRuleActions with the default rule from backend
  useEffect(() => {
    if (defaultRule && defaultRule.actions) {
      setDefaultRuleActions(defaultRule.actions.split(",").map((id) => parseInt(id.trim())));
    }
  }, [defaultRule]);

  /**
   * Handles the start of dragging a rule
   * @author Sarthak Arora
   */
  const handleDragStart = (e, rule) => {
    setDraggedRule(rule);
    e.dataTransfer.effectAllowed = "move";
    e.target.classList.add("dragging");
  };

  /**
   * Handles the end of dragging a rule
   * @author Sarthak Arora
   */
  const handleDragEnd = (e) => {
    e.target.classList.remove("dragging");
    setDraggedRule(null);
  };

  /**
   * Handles the drag over event
   * @author Sarthak Arora
   */
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  /**
   * Handles the dropping of a rule between sections
   * @author Sarthak Arora
   */
  const handleDrop = async (e, targetRule, isDeleteSection) => {
    e.preventDefault();
    if (!draggedRule) return;

    // Don't allow dropping between sections
    const draggedActionIds = draggedRule.actions.split(",").map((id) => parseInt(id.trim()));
    const draggedIsDelete = draggedActionIds.includes(DELETE_ACTION_ID);
    if (draggedIsDelete !== isDeleteSection) return;

    // Find the new position
    const currentSectionRules = isDeleteSection ? deleteRules : nonDeleteRules;
    const targetIndex = currentSectionRules.findIndex((r) => r.id === targetRule.id);
    const draggedIndex = currentSectionRules.findIndex((r) => r.id === draggedRule.id);

    // Create a new array with the updated order
    const updatedSectionRules = [...currentSectionRules];
    const [movedRule] = updatedSectionRules.splice(draggedIndex, 1);
    updatedSectionRules.splice(targetIndex, 0, movedRule);

    // Calculate new sortIds for all rules in the section
    const baseSortId = isDeleteSection ? 1 : deleteRules.length + 1;
    const updatedRules = updatedSectionRules.map((rule, index) => ({
      ...rule,
      sortOrder: baseSortId + index,
    }));

    try {
      const payload = {
        ruleId: draggedRule.id,
        sortId: targetIndex + 1 + (isDeleteSection ? 0 : deleteRules.length),
      };
      await updateEmailRule(payload);
      setRules((prevRules) => {
        const otherSectionRules = isDeleteSection ? nonDeleteRules : deleteRules;
        const allRules = [...otherSectionRules, ...updatedRules];
        return allRules.sort((a, b) => a.sortOrder - b.sortOrder);
      });
    } catch (error) {
      getNotification(labels.NOTIFICATIONS.RULE_UPDATE_FAILURE, notifyIcon.ERROR_ICON);
    }
  };

  /**
   * Handles the closing of the rule form
   * @author Sarthak Arora
   */
  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingRule(null);
  };

  /**
   * Handles the deletion of a rule
   * @author Sarthak Arora
   */
  const handleDelete = (ruleId) => {
    setRules(rules.filter((rule) => rule.id !== ruleId));
  };

  if (error) {
    return <div className='rules-error'>{error}</div>;
  }

  /**
   * Handles the toggling of a rule's disabled state
   * @author Sarthak Arora
   */
  const handleToggle = async (rule) => {
    try {
      const ruleId = rule.id;
      await updateEmailRule({ ruleId, isDisabled: !rule.isDisabled });
      setRules(rules.map((rule) => (rule.id === ruleId ? { ...rule, isDisabled: !rule.isDisabled } : rule)));
      getNotification(labels.NOTIFICATIONS.RULE_UPDATE_SUCCESS, notifyIcon.SUCCESS_ICON);
    } catch (error) {
      getNotification(labels.NOTIFICATIONS.RULE_UPDATE_FAILURE, notifyIcon.ERROR_ICON);
    }
  };

  /**
   * Handles the editing of an existing rule
   * @author Sarthak Arora
   */
  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setIsFormOpen(true);
  };

  /**
   * Handles saving a rule and refreshing the list
   * @author Sarthak Arora
   */
  const handleSaveRule = () => {
    handleCloseForm();
    refreshRules();
  };

  /**
   * Helper to get updated default actions for the default rule
   */
  const getUpdatedDefaultActions = (actionId) => {
    if (actionId === DELETE_ACTION_ID && !defaultRuleActions.includes(DELETE_ACTION_ID)) {
      return [DELETE_ACTION_ID];
    } else if (defaultRuleActions.includes(actionId)) {
      return defaultRuleActions.filter((id) => id !== actionId && id !== DELETE_ACTION_ID);
    } else {
      return [...defaultRuleActions.filter((id) => id !== DELETE_ACTION_ID), actionId];
    }
  };

  /**
   * Handles the changing of the default rule actions
   * @author Sarthak Arora
   */
  const handleDefaultActionChange = async (actionId) => {
    const updatedActions = getUpdatedDefaultActions(actionId);
    setDefaultRuleActions(updatedActions);

    // Call updateEmailRule for the default rule
    if (defaultRule) {
      try {
        await updateEmailRule({
          ruleId: defaultRule.id,
          actions: updatedActions.join(","),
        });
        getNotification(labels.NOTIFICATIONS.RULE_UPDATE_SUCCESS, notifyIcon.SUCCESS_ICON);
      } catch (error) {
        getNotification(labels.NOTIFICATIONS.RULE_UPDATE_FAILURE, notifyIcon.ERROR_ICON);
      }
    }
  };

  if (loading) {
    return <div className='px-4'>{labels.LOADING_RULES}</div>;
  }

  const handleAddRule = () => {
    setEditingRule(null);
    setIsFormOpen(true);
  };
  return (
    <div className='email-rule-engine col-lg-6 col-md-6 col-sm-12'>
      <div className='rules-container'>
        <div className='rules-heading d-flex align-items-center justify-content-between mb-2'>
          <h3 className="mb-0">{labels.RULES_TITLE}</h3>
          <button
            id='add-rule-btn'
            className='btn btn-sm btn-light-custom d-flex align-items-center'
            onClick={(e) => {
              e.stopPropagation();
              handleAddRule();
            }}>
            <span className='app-icon-medium'>{icon.ADD}</span>
            <span className='ml-1'>{labels.ADD_RULE_ONLY}</span>
          </button>
        </div>
        <div className='rules-list'>
          {/* Delete Rules Section */}
          {!!(deleteRules.length > 0) && (
            <div className='rules-section'>
              <h4>{labels.DELETE_ACTIONS_SECTION}</h4>
              <div className='rules-container'>
                {deleteRules.map((rule) => (
                  <div
                    key={rule.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, rule)}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, rule, true)}
                    className='rule-card-wrapper'>
                    <div className='drag-handle'>{icon.DRAG_INDICATOR}</div>
                    <RuleCard
                      rule={rule}
                      index={rules.indexOf(rule)}
                      onEdit={() => handleEditRule(rule)}
                      onDelete={handleDelete}
                      onToggle={handleToggle}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Non-Delete Rules Section */}
          {!!(nonDeleteRules.length > 0) && (
            <div className='rules-section'>
              <h4>{labels.OTHER_ACTIONS_SECTION}</h4>
              <div className='rules-container'>
                {nonDeleteRules.map((rule) => (
                  <div
                    key={rule.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, rule)}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, rule, false)}
                    className='rule-card-wrapper'>
                    <div className='drag-handle'>{icon.DRAG_INDICATOR}</div>
                    <RuleCard
                      rule={rule}
                      index={rules.indexOf(rule)}
                      onEdit={() => handleEditRule(rule)}
                      onDelete={handleDelete}
                      onToggle={handleToggle}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className='default-rule-section'>
            <h4>{labels.DEFAULT_RULE_SECTION}</h4>
            <ActionsSection
              formData={{ actions: defaultRuleActions }}
              actions={actions}
              isActionSelected={isDefaultActionSelected}
              handleActionChange={handleDefaultActionChange}
              errors={{}}
              isDefaultRule={true}
              defaultRuleId={defaultRule?.id}
            />
          </div>
        </div>
      </div>
      {isFormOpen && <RuleForm isOpen={isFormOpen} onClose={handleCloseForm} onSubmit={handleSaveRule} editingRule={editingRule} />}
    </div>
  );
};

export default RulesList;
